import {
  Button,
  MenuItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { EventsTable } from "features/admin/events/EventsTable";
import { useFundFamilyListSuspense } from "gen/endpoints/fund-family/fund-family";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import PageBody from "shared/components/layout/PageBody";
import PageContainer from "shared/components/layout/PageContainer";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import {
  ConfirmationDialogProps,
  useConfirmationDialog,
} from "shared/components/modal/confirm/ConfirmationDialog";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { TableBoxTitleMenu } from "shared/components/table/box/TableBoxTitleMenu";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { CheckIcon } from "shared/icons/CheckIcon";
import { rootRoute } from "shared/navigation/Routes";
import { NonNullStringParam } from "shared/utils/QueryParamUtils";
import { useQueryParam } from "use-query-params";
import { z } from "zod";

export const EventsPage: React.FC = () => {
  const { t } = useTranslation(["common", "events"]);
  const navigate = useNavigate();

  const [fundFamilyId, setFundFamilyId] = useQueryParam(
    "fundFamilyId",
    NonNullStringParam,
  );

  const selectFundFamilyForRollback = useConfirmationDialog(
    RollbackFundFamilyDialog,
    {
      fundFamilyId,
    },
  );

  const onClickRollback = async () => {
    const rollbackFundFamilyId = await selectFundFamilyForRollback();
    if (rollbackFundFamilyId) {
      navigate(
        rootRoute({}).admin({}).events({}).rollback({
          fundFamilyId: rollbackFundFamilyId,
        }).$,
      );
    }
  };

  return (
    <PageContainer>
      <PageContent isFullHeight={true}>
        <PageBody>
          <PageHeader
            title={t("common:events")}
            actions={
              <Button colorScheme="destructive" onClick={onClickRollback}>
                {t("events:createRollback")}
              </Button>
            }
          />
          <TableBox>
            <TableBoxHeader>
              <FundFamilyDropdown
                fundFamilyId={fundFamilyId}
                setFundFamilyId={setFundFamilyId}
              />
              <DataTableBoxActions />
            </TableBoxHeader>
            <TableBoxBody>
              <QueryBoundary>
                <EventsTable fundFamilyId={fundFamilyId} />
              </QueryBoundary>
            </TableBoxBody>
          </TableBox>
        </PageBody>
      </PageContent>
    </PageContainer>
  );
};

const FundFamilyDropdown: React.FC<{
  fundFamilyId: string | null | undefined;
  setFundFamilyId: (fundFamilyId: string | null | undefined) => void;
}> = ({ fundFamilyId, setFundFamilyId }) => {
  const { t } = useTranslation(["common"]);
  const { data } = useFundFamilyListSuspense();
  const selectedFundFamily = data.find(
    (fundFamily) => fundFamily.id === fundFamilyId,
  );
  return (
    <TableBoxTitle>
      <TableBoxTitleMenu
        title={selectedFundFamily ? selectedFundFamily.name : t("common:all")}
      >
        <MenuItem onClick={() => setFundFamilyId(undefined)}>
          {t("common:allFundFamilies")}
          {!fundFamilyId && <CheckIcon />}
        </MenuItem>
        {data.map((fundFamily) => (
          <MenuItem
            key={fundFamily.id}
            onClick={() => setFundFamilyId(fundFamily.id)}
          >
            {fundFamily.name}
            {fundFamilyId === fundFamily.id && <CheckIcon />}
          </MenuItem>
        ))}
      </TableBoxTitleMenu>
    </TableBoxTitle>
  );
};

const rollbackSchema = z.object({
  fundFamilyId: z.string().min(1),
});

type RollbackSchema = z.infer<typeof rollbackSchema>;

const RollbackFundFamilyDialog: React.FC<
  ConfirmationDialogProps<string, Partial<RollbackSchema>>
> = ({ alertProps, onSuccess, onCancel, extraProps: { fundFamilyId } }) => {
  const { t } = useTranslation(["common", "events"]);

  const { data } = useFundFamilyListSuspense();

  const form = useForm<RollbackSchema>({
    resolver: zodResolver(rollbackSchema),
    defaultValues: {
      fundFamilyId,
    },
  });

  const onSubmit = ({ fundFamilyId }: RollbackSchema) => {
    onSuccess(fundFamilyId);
  };

  return (
    <Modal scrollBehavior="inside" {...alertProps}>
      <ModalOverlay />
      <ModalContent>
        <Form form={form} onSubmit={onSubmit}>
          <ModalHeader>
            {t("events:createRollbackDialog.title")}
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <Text mb={4}>{t("events:createRollbackDialog.description")}</Text>
            <FormSelect name="fundFamilyId" title={t("common:fundFamily")}>
              {data.map((fundFamily) => (
                <SelectOption key={fundFamily.id} value={fundFamily.id!}>
                  {fundFamily.name}
                </SelectOption>
              ))}
            </FormSelect>
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={() => onCancel()} />}
              confirm={
                <FormSubmitButton>
                  {t("events:createRollbackDialog.submit")}
                </FormSubmitButton>
              }
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
